import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { useAuth } from '@/lib/auth/AuthProvider'
import { PERSONAL_EXPENSE_CATEGORIES } from './types'
import type { EntryInput, NoteInput, PersonalCategory, PersonalEntry, PersonalNote, PersonalSettings, PersonalSummary, PersonalWallet, SettingsInput, WalletInput } from './types'

function useOwner() {
  const { user } = useAuth()
  return user?.id ?? 'anonymous'
}

const keys = { root: (o: string) => ['personal-finance', o] as const }

function useMut<A>(fn: (arg: A) => Promise<unknown>) {
  const owner = useOwner()
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: keys.root(owner) }) })
}

function money(v: unknown) {
  if (typeof v === 'number' && Number.isFinite(v)) return v.toFixed(2)
  return typeof v === 'string' && v.trim() ? v : '0.00'
}

/** Ekran kategori anahtarı → API kategori adı (API Türkçe adları saklar). */
const CATEGORY_TO_API: Record<PersonalCategory, string> = {
  market: 'market',
  bills: 'faturalar',
  rent: 'kira',
  transport: 'ulaşım',
  health: 'sağlık',
  education: 'eğitim',
  entertainment: 'eğlence',
  clothing: 'giyim',
  other_expense: 'diğer gider',
  salary: 'maaş',
  bonus: 'prim',
  side: 'ek gelir',
  investment: 'yatırım',
  other_income: 'diğer gelir',
}

const CATEGORY_FROM_API: Record<string, PersonalCategory> = {
  ...Object.fromEntries(Object.entries(CATEGORY_TO_API).map(([k, v]) => [v, k as PersonalCategory])),
  ulasim: 'transport',
  saglik: 'health',
  egitim: 'education',
  eglence: 'entertainment',
  diger_gider: 'other_expense',
  maas: 'salary',
  ek_gelir: 'side',
  yatirim: 'investment',
  diger_gelir: 'other_income',
}

function categoryOf(v: unknown, kind: 'income' | 'expense'): PersonalCategory {
  const key = String(v ?? '').trim()
  return CATEGORY_FROM_API[key] ?? (key in CATEGORY_TO_API ? (key as PersonalCategory) : kind === 'income' ? 'other_income' : 'other_expense')
}

function mapWallet(row: Record<string, unknown>): PersonalWallet {
  const kind = row.kind ?? row.type
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    type: kind === 'bank' || kind === 'credit_card' ? kind : 'cash',
    bankName: String(row.bank_name ?? row.bankName ?? ''),
    last4: String(row.last4 ?? ''),
    opening: money(row.opening_balance ?? row.opening),
    balance: money(row.balance),
  }
}

function mapEntry(row: Record<string, unknown>): PersonalEntry {
  const kind = (row.direction ?? row.kind) === 'income' ? 'income' : 'expense'
  return {
    id: String(row.id ?? ''),
    kind,
    category: categoryOf(row.category, kind),
    amount: money(row.amount),
    date: String(row.txn_date ?? row.date ?? ''),
    walletId: String(row.wallet_id ?? row.walletId ?? ''),
    recurrence: row.recurrence === 'monthly' || row.recurrence === 'yearly' ? row.recurrence : 'once',
    status: row.status === 'pending' ? 'pending' : 'paid',
    description: String(row.description ?? ''),
    attachmentName: String(row.attachment_name ?? row.attachmentName ?? ''),
    source: row.source === 'payroll' ? 'payroll' : 'manual',
    locked: row.locked === true,
  }
}

function mapNote(row: Record<string, unknown>): PersonalNote {
  return {
    id: String(row.id ?? ''),
    title: String(row.title ?? ''),
    tags: Array.isArray(row.tags) ? row.tags.filter((t): t is string => typeof t === 'string') : [],
    body: String(row.body ?? ''),
    pinned: row.pinned === true,
    remindAt: typeof row.remind_at === 'string' ? row.remind_at : typeof row.remindAt === 'string' ? row.remindAt : null,
    attachmentName: String(row.attachment_name ?? row.attachmentName ?? ''),
    updatedAt: String(row.updated_at ?? row.updatedAt ?? row.created_at ?? ''),
  }
}

function mapSettings(row: Record<string, unknown>): PersonalSettings {
  return {
    autoSalary: row.auto_salary === true,
    salaryWalletId: String(row.salary_wallet_id ?? ''),
    syncFrom: String(row.sync_from ?? ''),
    employeeLinked: row.employee_linked === true,
  }
}

function walletBody(input: WalletInput) {
  return { name: input.name, kind: input.type, bank_name: input.bankName, last4: input.last4, opening_balance: input.opening }
}

function entryBody(input: EntryInput) {
  return {
    direction: input.kind,
    category: CATEGORY_TO_API[input.category],
    amount: input.amount,
    txn_date: input.date,
    wallet_id: input.walletId,
    recurrence: input.recurrence,
    status: input.status,
    description: input.description,
    attachment_name: input.attachmentName,
  }
}

function noteBody(input: NoteInput) {
  return { title: input.title, tags: input.tags, body: input.body, pinned: input.pinned, remind_at: input.remindAt, attachment_name: input.attachmentName }
}

export function usePersonalSummary(month: string) {
  const owner = useOwner()
  return useQuery({
    queryKey: [...keys.root(owner), 'summary', month],
    queryFn: async () => {
      const row = await api.get<Record<string, unknown>>('/api/v1/finance/personal/summary', { month })
      const byCategory = Array.isArray(row.categories) ? row.categories : []
      const wallets = Array.isArray(row.wallets) ? (row.wallets as Record<string, unknown>[]) : []
      const walletsTotal = wallets.reduce((sum, w) => sum + (Number(w.balance) || 0), 0)
      return {
        month: String(row.month ?? month),
        income: money(row.income),
        expense: money(row.expense),
        net: money(row.net),
        pending: { count: Number(row.pending_count) || 0, amount: money(row.pending_amount) },
        // API gelir+gider kategorilerini birlikte döner; grafik yalnız harcamaları gösterir.
        byCategory: byCategory
          .map((item) => item as Record<string, unknown>)
          .filter((cat) => (PERSONAL_EXPENSE_CATEGORIES as readonly string[]).includes(categoryOf(cat.category, 'expense')))
          .map((cat) => {
            const total = Number(row.expense) || 0
            const amount = Number(cat.amount) || 0
            return { category: categoryOf(cat.category, 'expense'), amount: money(cat.amount), pct: total > 0 ? Math.round((amount / total) * 1000) / 10 : 0 }
          }),
        walletsTotal: walletsTotal.toFixed(2),
      } satisfies PersonalSummary
    },
  })
}

export function usePersonalWallets() {
  const owner = useOwner()
  return useQuery({
    queryKey: [...keys.root(owner), 'wallets'],
    queryFn: async () => asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/finance/personal/wallets')).map(mapWallet),
  })
}

export function usePersonalEntries() {
  const owner = useOwner()
  return useQuery({
    queryKey: [...keys.root(owner), 'entries'],
    queryFn: async () => asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/finance/personal/transactions')).map(mapEntry),
  })
}

export function usePersonalNotes() {
  const owner = useOwner()
  return useQuery({
    queryKey: [...keys.root(owner), 'notes'],
    queryFn: async () => asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/finance/personal/notes')).map(mapNote),
  })
}

export function usePersonalSettings() {
  const owner = useOwner()
  return useQuery({
    queryKey: [...keys.root(owner), 'settings'],
    queryFn: async () => mapSettings(await api.get<Record<string, unknown>>('/api/v1/finance/personal/settings')),
  })
}

export const useSaveSettings = () =>
  useMut((input: SettingsInput) =>
    api.put('/api/v1/finance/personal/settings', { auto_salary: input.autoSalary, salary_wallet_id: input.salaryWalletId, sync_from: input.syncFrom }),
  )
export const useSaveWallet = () =>
  useMut(({ id, input }: { id?: string; input: WalletInput }) => (id ? api.put(`/api/v1/finance/personal/wallets/${id}`, walletBody(input)) : api.post('/api/v1/finance/personal/wallets', walletBody(input))))
export const useDeleteWallet = () => useMut((id: string) => api.del(`/api/v1/finance/personal/wallets/${id}`))
export const useSaveEntry = () =>
  useMut(({ id, input }: { id?: string; input: EntryInput }) =>
    id ? api.put(`/api/v1/finance/personal/transactions/${id}`, entryBody(input)) : api.post('/api/v1/finance/personal/transactions', entryBody(input)),
  )
/** Ödendi/bekliyor durumunu çevirir; API tam kayıt beklediği için mevcut kayıt gövdesiyle gönderilir. */
export const useToggleEntry = () =>
  useMut((entry: PersonalEntry) =>
    api.put(`/api/v1/finance/personal/transactions/${entry.id}`, entryBody({ ...entry, status: entry.status === 'pending' ? 'paid' : 'pending' })),
  )
export const useDeleteEntry = () => useMut((id: string) => api.del(`/api/v1/finance/personal/transactions/${id}`))
export const useSaveNote = () =>
  useMut(({ id, input }: { id?: string; input: NoteInput }) => (id ? api.put(`/api/v1/finance/personal/notes/${id}`, noteBody(input)) : api.post('/api/v1/finance/personal/notes', noteBody(input))))
export const useDeleteNote = () => useMut((id: string) => api.del(`/api/v1/finance/personal/notes/${id}`))
